import React, { useCallback, useMemo, useState } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { fetchAllDropdownListsStarted } from '../dropdown-lists/store';
import { deleteClientCalculatorPromptStarted, duplicateSystemPrompt, fetchClientCalculatorPromptsStarted, getAllClientCalculatorPrompts, getClientCalculatorPromptToDelete, getClientCalculatorPromptUpdated, getCurrentClientCalculatorPromptOrderForType, getIsDeletingClientCalculatorPrompts, getSavedClientCalculatorPromptOrderForType, getSelectedClientCalculatorPrompt, saveClientPromptOrderStarted, selectClientCalculatorPrompt, setClientCalculatorPromptOrder, toggleDeleteClientCalculatorPromptConfirmationModal, updateClientCalculatorPrompt, updateClientCalculatorPromptOrder, upsertClientCalculatorPromptStarted } from './store';
import { CalculatorPromptDB, CalculatorPromptUI, getAllCalculatorFields } from '../calculator/store';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { Calculator, CaretDown, CaretSide } from '../../shared/icons';
import { PlusButton } from '../../shared/button/PlusButton';
import styles from './ClientCalculatorPrompt.module.scss';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { TypeDeleteConfirmationModal } from '../../shared/modal/TypeDeleteConfirmationModal';
import { Toggle } from '../../shared/toggle';
import { CalculatorPromptReorderModal } from '../shared/CalculatorPromptReorderModal';
import { CalculatorPromptModal } from '../shared/CalculatorPromptModal';
import { CalculatorPrompts } from '../shared/CalculatorPrompts';

const NAV_HEADER_MARGINS = 160;

export const ClientCalculator: React.FC = () => {
    useFetchStarted([fetchClientCalculatorPromptsStarted(), fetchAllDropdownListsStarted()]);
    const [openSections, setOpenSections] = useState<string[]>([]);
    const [reorderModalOpen, setReorderModalOpen] = useState<boolean>(false);
    const [showSystemPrompts, setShowSystemPrompts] = useState<boolean>(true);

    const dispatch = useAppDispatch();
    const fieldTypeOptions = useAppSelector(getAllCalculatorFields);
    const calculatorPrompts = useAppSelector(getAllClientCalculatorPrompts);
    const selectedCalculatorPrompt = useAppSelector(getSelectedClientCalculatorPrompt);
    const calculatorPromptIdToDelete = useAppSelector(getClientCalculatorPromptToDelete);
    const isDeleting = useAppSelector(getIsDeletingClientCalculatorPrompts);
    const currentTypePromptOrder = useAppSelector(getCurrentClientCalculatorPromptOrderForType);
    const savedTypePromptOrder = useAppSelector(getSavedClientCalculatorPromptOrderForType);
    const hasUpdated = useAppSelector(getClientCalculatorPromptUpdated);
    const testId = 'client-calculator-prompt';

    const savePromptOrder = useCallback(() => dispatch(saveClientPromptOrderStarted()), [dispatch]);
    const setPromptOrder = useCallback((promptOrder: CalculatorPromptDB[]) => dispatch(setClientCalculatorPromptOrder(promptOrder)), [dispatch]);
    const updatePromptOrder = useCallback((promptOrder: CalculatorPromptDB[]) => dispatch(updateClientCalculatorPromptOrder(promptOrder)), [dispatch]);
    const openReorderModal = useCallback((promptOrder: CalculatorPromptDB[]) => {
        const promptToReorder = promptOrder.filter(({ isSystem }) => !isSystem);
        setReorderModalOpen(true);
        setPromptOrder(promptToReorder);
    }, [setReorderModalOpen, setPromptOrder]);
    const closeReorderModal = useCallback(() => {
        setReorderModalOpen(false);
        setPromptOrder([]);
    }, [setReorderModalOpen, setPromptOrder]);

    const selectPrompt = useCallback((calculatorPromptId?: number) => dispatch(selectClientCalculatorPrompt(calculatorPromptId)), [dispatch]);
    const createNewPrompt = useCallback(() => selectPrompt(), [selectPrompt]);

    const deletePrompt = useCallback(() => dispatch(deleteClientCalculatorPromptStarted()), [dispatch]);
    const toggleDeleteConfirmationModal = useCallback((calculatorPromptId: number | null) => dispatch(toggleDeleteClientCalculatorPromptConfirmationModal(calculatorPromptId)), [dispatch]);

    const [screenWidth, screenHeight] = useWindowResize();

    const wrapperHeight = useMemo(() => screenHeight - NAV_HEADER_MARGINS, [screenHeight]);
    const clientCalculatorPrompts = useMemo(() => calculatorPrompts.filter(({ isSystem }) => !isSystem), [calculatorPrompts]);
    const systemCalculatorPrompts = useMemo(() => calculatorPrompts.filter(({ isSystem }) => isSystem), [calculatorPrompts]);

    const getRelevantPrompts = useCallback((fieldType: string) => {
        const clientPrompts = clientCalculatorPrompts.filter(({ calculatorFieldType }) => calculatorFieldType === fieldType).sort((a, b) => a.priorityOrder - b.priorityOrder);
        const systemPrompts = systemCalculatorPrompts.filter(({ calculatorFieldType }) => calculatorFieldType === fieldType).sort((a, b) => a.priorityOrder - b.priorityOrder);
        return showSystemPrompts ? [...clientPrompts, ...systemPrompts] : clientPrompts;
    }, [showSystemPrompts, clientCalculatorPrompts, systemCalculatorPrompts]);

    const getRelevantClientPrompts = useCallback((fieldType: string) => {
        const clientPrompts = clientCalculatorPrompts.filter(({ calculatorFieldType }) => calculatorFieldType === fieldType).sort((a, b) => a.priorityOrder - b.priorityOrder);
        return clientPrompts;
    }, [clientCalculatorPrompts]);

    const getSectionOpen = useCallback((calculatorFieldType: string) => openSections.includes(calculatorFieldType), [openSections]);
    const getSectionOpenIcon = useCallback((isOpen: boolean) => isOpen ? CaretDown : CaretSide, []);
    const toggleSection = useCallback((calculatorFieldType: string) => {
        const isOpen = getSectionOpen(calculatorFieldType);
        if (isOpen) {
            return setOpenSections(openSections => openSections.filter(type => type !== calculatorFieldType));
        }
        return setOpenSections(openSections => ([...openSections, calculatorFieldType]));
    }, [getSectionOpen, setOpenSections]);

    const duplicatePrompt = useCallback((promptId: number) => {
        const promptToDuplicate = systemCalculatorPrompts.find(prompt => prompt.promptId === promptId)!;
        dispatch(duplicateSystemPrompt(promptToDuplicate));
    }, [systemCalculatorPrompts, dispatch]);

    const closeModal = useCallback(() => dispatch(selectClientCalculatorPrompt(null)), [dispatch]);
    const updatePrompt = useCallback((calculatorPrompt: CalculatorPromptUI) => dispatch(updateClientCalculatorPrompt(calculatorPrompt)), [dispatch]);
    const saveCalculatorPrompt = useCallback(() => dispatch(upsertClientCalculatorPromptStarted()), [dispatch]);

    return (
        <div className={styles.calculatorPromptWrapper} data-testid={`${testId}-wrapper`}>
            <div className={styles.calculatorPromptHeaderWrapper}>
                <div className={styles.calculatorPromptTitleButtonWrapper}>
                    <div className={styles.calculatorPromptHeaderTitle} data-testid={`${testId}-title`}>Calculator Prompts</div>
                    <PlusButton onClick={createNewPrompt} testId={testId} fontSize={22} />
                    <div className={styles.systemPromptsToggleWrapper}>
                        <div className={styles.systemPromptsToggleLabel}>Include System Prompts</div>
                        <Toggle
                            onChange={() => setShowSystemPrompts(!showSystemPrompts)}
                            checked={showSystemPrompts}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.calculatorPromptContent} style={{ height: wrapperHeight }}>
                <div className={styles.calculatorPromptsWrapper}>
                    <Scrollable>
                        {fieldTypeOptions.map(({ includeGracePeriod, includeOutput, name, calculatorFieldTypeId }) => {
                            const sectionOpen = getSectionOpen(name);
                            const relevantPrompts = getRelevantPrompts(name);
                            const showReorderIcon = relevantPrompts.filter(({ isSystem }) => !isSystem).length > 1;
                            const showButtons = (isSystem: boolean) => !isSystem;
                            return (
                                <CalculatorPrompts
                                    key={calculatorFieldTypeId}
                                    name={name}
                                    sectionOpen={sectionOpen}
                                    showReorderIcon={showReorderIcon}
                                    relevantPrompts={relevantPrompts}
                                    includeOutput={includeOutput}
                                    includeGracePeriod={includeGracePeriod}
                                    openReorderModal={openReorderModal}
                                    duplicatePrompt={duplicatePrompt}
                                    showButtons={showButtons}
                                    selectPrompt={selectPrompt}
                                    toggleDeleteConfirmationModal={toggleDeleteConfirmationModal}
                                    getSectionOpenIcon={getSectionOpenIcon}
                                    toggleSection={toggleSection}
                                />
                            );
                        }
                        )}
                    </Scrollable>
                </div>
            </div>
            {!isNull(selectedCalculatorPrompt) &&
                <CalculatorPromptModal
                    width={screenWidth * 0.7}
                    calculatorPrompt={selectedCalculatorPrompt}
                    getRelevantPrompts={getRelevantClientPrompts}
                    hasUpdated={hasUpdated}
                    closeModal={closeModal}
                    updatePrompt={updatePrompt}
                    saveCalculatorPrompt={saveCalculatorPrompt}
                />
            }
            <CalculatorPromptReorderModal
                isOpen={reorderModalOpen}
                closeModal={closeReorderModal}
                saveOrder={savePromptOrder}
                setOrder={updatePromptOrder}
                savedPromptOrder={savedTypePromptOrder}
                currentPromptOrder={currentTypePromptOrder}
            />
            <TypeDeleteConfirmationModal
                isOpen={!isNull(calculatorPromptIdToDelete)}
                closeModal={() => toggleDeleteConfirmationModal(null)}
                deleteAction={deletePrompt}
                headerLabel='Delete Calculator Prompt'
                icon={Calculator}
                isDeleting={isDeleting}
                testId={testId}
                deleteLabel='calculator prompt'
                name=''
                warning='This cannot be undone and will be lost as a few shot example for this calculator field type'
            />
        </div>
    );
};
